// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slide, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch } from 'Hooks';
import { createNewFolderThunk, updateFolderThunk } from 'Features/folderList/folderListThunk';
import { fieldErrorMessage } from 'Utils/apiResponse';
import stylesDef from './styles';

interface CreateFolderDialogProps {
  showDialog: boolean;
  title?: string;
  isEdit?: boolean;
  folderName?: string;
  folderId?: string;
  setShowDialog: (show: boolean) => void;
}

const Transition = React.forwardRef<unknown, TransitionProps>((props, ref) => <Slide ref={ref} direction="up" {...props} />);

const CreateFolderDialog = ({ showDialog, title = 'New Folder', isEdit = false, folderName, folderId, setShowDialog }: CreateFolderDialogProps) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  // State Variables
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // useEffect
  useEffect(() => {
    if (isEdit && folderName) {
      setNewFolderName(folderName);
    } else {
      setNewFolderName('');
    }
    setError('');
  }, [isEdit, folderName, showDialog]);

  // Methods
  const handleFolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewFolderName(e.target.value);
    setError('');
  };

  const handleCreateFolder = () => {
    // Validate folder name
    if (newFolderName.trim() && !loading) {
      // If editing and folderId is available rename the folder; otherwise, create a new folder
      if (isEdit && folderId) {
        renameFolder(folderId, newFolderName.trim());
      } else {
        createFolder(newFolderName.trim());
      }
    }
  };

  // A rejected name (e.g. already taken by a project) is shown under the field; any other failure
  // shows the generic message for the action.
  const saveFolder = async (save: () => Promise<IApiResponse<IFolder>>, fallbackError: string) => {
    setLoading(true);
    try {
      const result = await save();
      if (!result.success) {
        setError(fieldErrorMessage(result, 'name', fallbackError));
        return;
      }
      setNewFolderName('');
      setShowDialog(false);
    } catch (e) {
      setError(fallbackError);
    } finally {
      setLoading(false);
    }
  };

  const createFolder = (name: string) => saveFolder(() => dispatch(createNewFolderThunk(name)), 'Failed to create folder');

  const renameFolder = (id: string, name: string) => saveFolder(() => dispatch(updateFolderThunk(id, name)), 'Failed to rename folder');

  return (
    <Dialog
      open={showDialog}
      onClose={() => setShowDialog(false)}
      TransitionComponent={Transition}
      keepMounted={false}
      sx={styles.dialog}
    >
      <Box sx={styles.titleSection}>
        <DialogTitle sx={styles.dialogTitle}>
          {title}
        </DialogTitle>
        <IconButton
          color="inherit"
          onClick={() => setShowDialog(false)}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1" sx={styles.dialogText}>
          Please name your folder:
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Folder Name"
          placeholder="Enter folder name"
          variant="outlined"
          value={newFolderName}
          inputProps={{ 'aria-label': 'Folder Name' }}
          onChange={handleFolderNameChange}
          onKeyDown={(e) => {
            // Enter saves, like the Save button; the dialog itself closes on Escape
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCreateFolder();
            }
          }}
          error={!!error}
          helperText={error}
          sx={styles.textField}
        />
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={() => setShowDialog(false)} sx={styles.actionButtons}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateFolder}
          disabled={!newFolderName.trim() || loading}
          variant="contained"
          sx={styles.actionButtons}
        >
          {loading ? (
            <CircularProgress size={24} sx={styles.buttonProgress} />
          ) : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateFolderDialog.defaultProps = {
  title: 'New Folder',
  isEdit: false,
  folderName: '',
  folderId: '',
};

export default CreateFolderDialog;
